/**
 * lib/giftCardPdf.ts — a branded gift-card voucher PDF (A5 landscape).
 */
import { jsPDF } from 'jspdf';
import type { GiftCard } from './types';
import type { Business } from '@/data/site';

const ESPRESSO_DEEP: [number, number, number] = [46, 27, 16];
const GOLD: [number, number, number] = [201, 150, 46];
const CREAM: [number, number, number] = [243, 234, 216];
const MUTED: [number, number, number] = [200, 188, 168];

const pula = (n: number) =>
  `P ${Number(n || 0).toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function generateGiftCardPdf(card: GiftCard, biz: Business): jsPDF {
  const d = new jsPDF({ unit: 'mm', format: 'a5', orientation: 'landscape' });
  const W = 210;
  const H = 148;

  d.setFillColor(...ESPRESSO_DEEP);
  d.rect(0, 0, W, H, 'F');
  d.setDrawColor(...GOLD);
  d.setLineWidth(0.6);
  d.rect(8, 8, W - 16, H - 16);

  // lotus
  d.setFillColor(...GOLD);
  const cx = W / 2;
  d.triangle(cx, 22, cx - 4, 32, cx + 4, 32, 'F');
  d.triangle(cx - 7, 25, cx - 10, 32, cx - 1, 32, 'F');
  d.triangle(cx + 7, 25, cx + 10, 32, cx + 1, 32, 'F');

  d.setFont('times', 'normal');
  d.setTextColor(...CREAM);
  d.setFontSize(26);
  d.text(biz.name, cx, 46, { align: 'center' });
  d.setFont('helvetica', 'normal');
  d.setFontSize(9);
  d.setTextColor(...GOLD);
  d.text('GIFT VOUCHER', cx, 54, { align: 'center', charSpace: 3 });

  d.setFont('times', 'bold');
  d.setFontSize(40);
  d.setTextColor(...GOLD);
  d.text(pula(card.amount), cx, 78, { align: 'center' });

  d.setFont('helvetica', 'normal');
  d.setFontSize(11);
  d.setTextColor(...CREAM);
  if (card.recipientName) d.text(`For: ${card.recipientName}`, cx, 92, { align: 'center' });
  if (card.fromName) d.text(`From: ${card.fromName}`, cx, 99, { align: 'center' });
  if (card.message) {
    d.setFont('times', 'italic');
    d.setFontSize(10);
    d.setTextColor(...MUTED);
    d.text(d.splitTextToSize(`“${card.message}”`, W - 60), cx, 110, { align: 'center' });
  }

  if (card.code) {
    d.setFont('courier', 'bold');
    d.setFontSize(13);
    d.setTextColor(...GOLD);
    d.text(card.code, cx, 126, { align: 'center', charSpace: 2 });
  }
  d.setFont('helvetica', 'normal');
  d.setFontSize(7.5);
  d.setTextColor(...MUTED);
  d.text(`Redeem at ${biz.name} · ${biz.address} · ${biz.phone}`, cx, 138, { align: 'center' });

  return d;
}

export const giftCardFilename = (card: GiftCard) => `${card.code || 'gift-card'}.pdf`;
export function giftCardBase64(card: GiftCard, biz: Business): string {
  return generateGiftCardPdf(card, biz).output('datauristring').split(',')[1] ?? '';
}
export function downloadGiftCard(card: GiftCard, biz: Business): void {
  generateGiftCardPdf(card, biz).save(giftCardFilename(card));
}
